import { FixedEventDef } from '../types';

/**
 * 固定剧情事件表（2026-10-06 定稿，设计基准 docs/特殊剧本实装设计.md §2.3）
 *
 * 两类来源、一套结构：
 *  - 全局事件：trigger 触发（calendar 日历窗口 / stage 咖位 / requireFlags 剧情链……），
 *    PlotSystem 每月 checkPending 扫描；
 *  - 剧本钩子事件：trigger 留空，由 SpecialScripts.plotHooks 命中（cast/done/airStart/rating）
 *    + afterMonths 延迟 + cond 条件触发。
 *  - 通告钩子事件（2026-10-24 加）：trigger 留空 + repeatable，由 NOTICE_PLOT_HOOKS 命中
 *    （通告生命周期信号×概率，所有同类通告共享；与上一类的分工见 NoticePlotHooks.ts 表头）。
 *
 * 三载体：encounter 偶遇 VN / message 消息 / media 媒体弹窗（三者都入 plotMessages 队列落档；
 * 承载 UI 批 3 按需现做，⛔ 写「人脉收件箱」——人脉页只管聊天消息，与剧情队列无关，2026-10-11 茄子令）。
 * 多步剧情用 steps（按序演出，分支=步级 requireFlags+选项 setFlags）。
 *
 * 文案现状（2026-10-14 茄子令「把占位的剧情全删了」）：除 SCRIPT_901《颠倒姻缘》五段正式文案
 * （茄子 10-10 手写，源文档 docs/特殊剧情/颠倒姻缘.md）外，本表只收有正式文案的事件。
 * 承天/无此镇/十二阶的六段【链路验证·临时文案】连同 SpecialScripts 里对应的钩子一并删除——
 * 那三本的剧情文案一句还没写，留着等于让 AI 占位文进玩家屏幕。
 * 要恢复：先落文案，再按 一一对应 补钩子（有钩子没事件＝fire 静默丢弃）。
 *
 * 2026-10-21 新增审查线三段（1.1 立约 / 1.2 通过回响 / 1.3 未通过），源文档 docs/主线剧情/审查线剧情文案.md。
 * 三段的宿主都是 `playOn: 'stageStory'`（带场景底图的全屏演出层），宿主查表口径见 `PlotSystem.checkStageStory`。
 */
export const FIXED_EVENTS: FixedEventDef[] = [
    {
        // 【正式文案 2026-10-19 茄子手写】承天片场初遇（源文档 docs/特殊剧情/承天.md 事件 1）
        // 触发口：绑定《承天》进组钩子（plotHooks cast）——开拍第一周，进组即触发；无 trigger（剧本钩子事件）。
        // 玩家已凭 PLOT_UNLOCK_SCRIPT_902 试镜进组、演苏合（掌香女官），与庄文茵（萧燕燕）有贴身对手戏。
        // 结识由 meetNpcs 完成；庄文茵在 NPCS 主表与 SPECIAL_NPCS 附表均已登记。
        id: 'FIXED_902_MEET',
        medium: 'encounter',
        npcId: 'npc_zhuangwenyin',
        description: '承天，草原片场。风大，旗子猎猎作响。\n你演苏合，太后帐下的掌香女官。今天的戏不难：夜里进帐回话，一句话，一个躬身。\n导演：停。再来。\n你退回去，重新推门。\n导演：停。你这演法是进去等我说戏的，不是去回话的。\n第三条。\n导演：……都先歇十分钟。\n你走到场边蹲下，把台词又看了一遍。旁边有人拖过一把椅子，坐下。\n庄文茵：你是绷着的。\n你抬头。是饰演萧燕燕的那位——庄文茵本人。\n你：……嗯。我怕演不对。\n庄文茵：苏合不是头一回进这个帐。灯搁在哪、案几在第几步，她闭着眼都摸得到。你刚才进门，像头一回来。\n庄文茵：想个你熟到不用想的地方——每天回的那间屋，摸黑也知道开关在哪。带着那个脚步进去。\n你：可我一进门就想着镜头。\n庄文茵：那就想手里端着香。先顾着别洒了，别想镜头。她是去上工，不是去朝见。\n你：……我试试。\n她没再说话。\n再开机。你推门进去，脚步落得很稳。\n导演：过。\n场记在板子上打了个勾。你松了口气，想转身道谢——\n庄文茵：你叫什么？\n你：%玩家名%。\n庄文茵：%玩家名%。（她念了一遍，点头）我记住了。',
        meetNpcs: ['npc_zhuangwenyin'],
        setFlags: ['FLAG_902_MET'],
    },
    {
        // 【正式文案 2026-10-19 茄子手写】承天开播首月群嘲（源文档 docs/特殊剧情/承天.md 事件 2）
        // 双入口：plotHook(airStart，玩家参演时先入队) ＋ trigger.scriptAirStart(SCRIPT_902，月底扫「该剧在播」，
        // 玩家/NPC 任一方播出都算)。不设热度门槛（10-14 令：airStart 只表示时机，不当闸门）。
        // 参演分层：步骤 1 热帖只给参演玩家；步骤 2 新闻卡全网可见（没参演的人从新闻卡开始看）。
        // 两拍均为舆论背景板，不结算玩家数值（buzz 只做话题度量级锚）。
        id: 'FIXED_902_BOOM',
        medium: 'media',
        playOn: 'decision',
        trigger: { scriptAirStart: 'SCRIPT_902' },
        steps: [
            {
                medium: 'media',
                onlyIfCast: 'SCRIPT_902',
                media: {
                    kind: 'hotpost',
                    buzz: 250,
                    title: 'rt庄文茵下凡第一部剧就这？《承天》给我看笑了',
                    body: '冲庄文茵来的，开屏雷击\n她这个状态真演不了十六岁，脸绷得像刚做完项目，苹果肌比台词还抢戏，说她是刚进宫的谁信啊，这分明是先帝的妃子回宫养老。。。\n还有和耶律斜轸那条感情线，正剧里搞这种剧情，何意味。。。',
                    comments: [
                        '观众的眼睛没惹任何人',
                        '四十几岁还硬凹少女，扮嫩看得我脚趾抠地，女明星什么时候可以接受自己从容老去',
                        '这剧一到感情线就像在看中老年爱情',
                    ],
                },
            },
            {
                medium: 'media',
                media: {
                    kind: 'news',
                    buzz: 300,
                    title: '《承天》开播口碑两极：庄文茵演少女引争议，剧情节奏遭诟病',
                    body: '庄文茵的首部电视剧《承天》开播后争议不断，口碑迅速走向两极。剧集的实景拍摄与朝堂群戏获得部分观众认可；但庄文茵在开篇饰演十六岁少女的段落连日被吐槽"年龄错位""出戏"，相关话题持续占据热议榜。此外，剧中为太后与耶律斜轸的旧情线、以及前段剧情节奏拖沓，亦被观众认为削弱了正剧质感。',
                    comments: [],
                },
            },
        ],
        setFlags: ['FLAG_902_BOOM'],
    },
    {
        // 【正式文案 2026-10-10 茄子手写；2026-10-18 茄子改：外套→毛衣、心理句改「哪有这么拉投资的」】
        // 颠倒姻缘主初遇：咖啡厅相遇（源文档 docs/特殊剧情/颠倒姻缘.md 事件 1）
        // 触发口（2026-10-13 茄子定「地点事件必须到访才触发」）：只有 ①咖啡厅访问指名派发（FreeroamMap.enterShop 调 PlotSystem.meetEventAtLocation）；
        //   ②日历扫描一见 trigger.location 就跳过这条（扫描侧不看地点，留着等于把「去不去」这把尺收走）
        // 结识由 meetNpcs 完成；解锁《颠倒姻缘》试镜资格由 unlockScripts 完成
        id: 'FIXED_901_MEET',
        medium: 'encounter',
        npcId: 'npc_xulinhui',
        trigger: {
            calendar: { from: { year: 1, month: 7 }, to: { year: 1, month: 12 } },
            location: 'shop_cafe',   // 拾光咖啡馆（地点 ID 已确认，见 Locations.ts）
        },
        description: '咖啡厅，下午。你坐在角落看剧本。\n隔壁桌，一个穿旧毛衣的男人正把一沓纸推给对面的人。\n？？？：……灵魂互换，古装，甜宠。您看看。\n对面的人：小许，我直说了。你这东西，没人看。\n？？？：嗯。\n对面的人：你上一部亏的还没补上呢。我也很难做。\n？？？：嗯，我知道。\n对面的人离开。那人坐在原位，没动。\n你：（哪有这么拉投资的。）\n那人抬头，目光停在你身上。\n？？？：那个……不好意思，你是演员吗？\n你：……算是吧。\n许林辉：我叫[[许林辉]]，是网剧《颠倒姻缘》的导演。那个……你要不要来试镜？\n你：（这人自己刚被拒了，转头就来拉人。）\n许林辉：不用现在定，你先看看。来了再说，角色还没完全定。',
        meetNpcs: ['npc_xulinhui'],
        setFlags: ['FLAG_901_MET'],
        unlockScripts: ['SCRIPT_901'],
    },
    {
        // 【正式文案 2026-10-10 茄子手写】试镜通知：初遇当月月底推送（requireFlags 接初遇）
        id: 'FIXED_901_NOTICE',
        medium: 'message',
        npcId: 'npc_xulinhui',
        trigger: { requireFlags: ['FLAG_901_MET'] },
        description: '许林辉：剧本我已经发给你经纪人了，你可以直接来试镜。',
    },
    {
        // 【正式文案 2026-10-10 茄子手写】颠倒姻缘片场：开拍第一周（plotHooks cast 命中——进组即触发）
        id: 'FIXED_901_SHOOT',
        medium: 'encounter',
        npcId: 'npc_xulinhui',
        description: '拍摄中。你换了戏服，布料有点扎人。\n你：（低头看袖子）这衣服什么情况？\n服装师：你就当它是丝绸。\n你：有点像窗帘。\n服装师：它就是窗帘。\n许林辉：（从旁边走过）能穿就行。\n你：导演，那个布景的窗户……\n许林辉：纸板。\n你：花瓶？\n许林辉：塑料的。别碰，碰了要赔。\n你：赔多少？\n许林辉：十五。\n远处，一个演员的假发歪了，造型师正用双面胶固定。\n许林辉面不改色。\n你：（他是怎么做到这么平静的。）',
        setFlags: ['FLAG_901_SHOOT'],
    },
    {
        // 【正式文案 2026-10-10 茄子手写】颠倒姻缘杀青钩子：片场夜（plotHooks done 命中）。
        // 2026-10-14 拆 ID：原与批 3 的 message 占位版同用 FIXED_901_DONE，eventById 的 find 只命中
        // 前者，这段成了永远演不到的死数据——独立成 FIXED_901_DONE_NIGHT；占位版当日已删（茄子令）。
        id: 'FIXED_901_DONE_NIGHT',
        medium: 'encounter',
        npcId: 'npc_xulinhui',
        description: '杀青。片场灯一盏盏灭，工作人员在收器材。\n你路过监视器，看见许林辉还坐在那儿。屏幕是黑的。\n你：还不走？\n许林辉：嗯，坐会儿。\n你在他旁边坐下。\n许林辉：拍完了。\n你：拍完了。\n许林辉：……你觉得能播吗。\n你：不知道。你呢。\n许林辉：不知道。（顿了顿）播了也没人看吧。\n你：（他自己都不信。）\n许林辉站起来，把监视器上那杯没喝完的咖啡拿起来，喝了一口。凉的。他皱了皱眉，还是喝完了。\n许林辉：行了，走吧。\n你：你接下来有什么打算？\n许林辉：不知道。先把这个剪完。然后……再说。\n你们往外走。他在门口停了一下。\n许林辉：那个，谢谢你啊。\n你：谢什么？\n许林辉：来拍这个戏。\n他挠了挠头，没再多说，就走了。',
        favor: { npc_xulinhui: 10 },
        setFlags: ['FLAG_901_DONE'],
    },
    {
        // 【正式文案 2026-10-10 茄子手写】颠倒姻缘开播钩子：四拍连演（plotHooks airStart 命中＝开播当月月末发信号；
        // 这条 when 原名 airBoom「热爆」，10-14 茄子令改名开播——它触发的就是「开播三天/上线一周」那两帖）
        // 四拍＝热议帖(media)、新闻卡(media)、小助理点评(encounter·人人，热度+50 走 plotHeatBonus 无封顶、名气按角色位 50/30)、
        // 许林辉私信(message·人人，10-14 茄子令拆双步：女主/非女主各一条，许林辉称呼玩家所演角色名)。
        // 第 3 拍 10-14 茄子更正由 message 改 VN：它是替玩家把前两帖读一遍的解说。
        // 【正式文案 2026-10-14 茄子令】本段改到决策页演：点「确认行程」之后、颁奖夜之后、行程执行之前
        // （playOn='decision'）。播出侧那类剧情今后都不在月度执行过程中触发，执行档只留地点到访与拍摄通告两类。
        // 【2026-10-18 茄子令】本事件双入口：plotHook(airStart，玩家参演时先入队) ＋
        // trigger.scriptAirStart(SCRIPT_901，月底扫「该剧在播」，玩家/NPC 任一方播出都算)。
        // 参演分层：步骤 1 热议帖只给参演玩家；步骤 2 新闻卡全网可见（没参演的人从新闻卡开始看）；
        // 后三拍（小助理点评/许林辉私信）带 onlyIfCast:'SCRIPT_901'，未参演整步跳过。
        id: 'FIXED_901_BOOM',
        medium: 'media',
        playOn: 'decision',
        trigger: { scriptAirStart: 'SCRIPT_901' },
        steps: [
            {
                medium: 'media',
                onlyIfCast: 'SCRIPT_901',   // 步骤 1 热议帖只给参演玩家（步骤 2 新闻卡仍全网）
                media: {
                    kind: 'hotpost',
                    buzz: 300,      // 话题度（讨论/转发/楼层点赞的量级锚，2026-10-17 茄子定 300）
                    title: '这剧的经费是不是全花在女主脸上了？《颠倒姻缘》穷得好笑但我停不下来',
                    body: '开播三天，热度{heat}。本来点进去是为了吐槽，结果一口气看了八集。女主是真的好看，衣服是真的窗帘。侯府的窗户，纸板。花瓶，塑料的。穷是真的穷，好笑也是真的好笑。',
                    comments: [
                        '纸板窗户我真的笑死了哈哈哈哈哈哈',
                        '剧情比同期几部大制作有意思多了，演技也没拖后腿',
                        '制作粗糙成这样也吹？观众门槛真低',
                    ],
                },
            },
            {
                medium: 'media',
                media: {
                    kind: 'news',
                    buzz: 430,      // 话题度（讨论/转发的量级锚，2026-10-17 茄子定 430）
                    title: '《颠倒姻缘》小成本逆袭，导演许林辉是谁？',
                    body: '零宣传、无明星的小成本网剧《颠倒姻缘》上线一周热度突破{heat}。导演许林辉此前仅有一部独立短片获奖，业内几乎无人知晓。据称该剧制作成本极低，多处布景服装被观众调侃"穷得坦荡"。目前已有多个影视公司开始接触许林辉团队。',
                    comments: [],
                },
            },
            {
                // 10-14 茄子更正：这一句在 VN 对话框里演＝对上面那两帖的评价/解释，不是月底投递的 message
                medium: 'encounter',
                npcId: 'assistant',
                onlyIfCast: 'SCRIPT_901',   // 没演这部剧的人不演这句（它是替参演玩家点评自己的剧）
                description: '太好了{player}，《颠倒姻缘》竟然爆了！',
                scriptHeat: [{ scriptId: 'SCRIPT_901', delta: 50 }],
                roleEffects: [
                    { onlyRole: '柳惊蛰', effects: { fame: 50 } },
                    { onlyRole: '!柳惊蛰', effects: { fame: 30 } },
                ],
            },
            {
                // 许林辉私信·女主玩家（柳惊蛰）——10-14 茄子令：拆双步，女主/非女主各一条，
                // 许林辉称呼玩家所演角色名；"也祝贺你"走 reply（玩家选完选项后 NPC 再回复）。
                // 10-15 茄子令：正文按 docs/特殊剧情/颠倒姻缘.md 的三段独白拆三颗气泡（换行=分颗，
                // 文案照文档原句，不并句不改点号）
                medium: 'message',
                npcId: 'npc_xulinhui',
                onlyRole: '柳惊蛰',
                onlyIfCast: 'SCRIPT_901',   // 只发给演了这部剧的玩家
                description: '那个……剧好像播得还行。\n好多人找我。还有公司打电话来。\n有点高兴。但说实话，我到现在都没太缓过来。',
                choices: [{ text: '恭喜你呀，这次真的被看见了。', reply: '也祝贺你，柳惊蛰。' }],
            },
            {
                // 许林辉私信·非女主玩家（李令仪等）——同上拆步
                medium: 'message',
                npcId: 'npc_xulinhui',
                onlyRole: '!柳惊蛰',
                onlyIfCast: 'SCRIPT_901',   // 只发给演了这部剧的玩家
                description: '那个……剧好像播得还行。\n好多人找我。还有公司打电话来。\n有点高兴。但说实话，我到现在都没太缓过来。',
                choices: [{ text: '恭喜你呀，这次真的被看见了。', reply: '也祝贺你，李令仪。' }],
            },
        ],
        setFlags: ['FLAG_901_BOOM'],
    },
    {
        // 【正式文案 2026-10-21 茄子手写；2026-10-25 挪时点+锚定句】审查一·开局立约《妈妈的电话》（源文档 docs/主线剧情/审查线剧情文案.md §1.1）
        // 触发口＝决策日入场扫描（`app.show('decision')` 先调 `PlotSystem.checkStageStory`）。2026-10-25 茄子定：
        // 立约戏挪出建角流程，改在**第 1 年 1 月的月度执行结束**（月末夜晚的语境与出租屋底图咬合）——
        // 月末结算先 advanceMonth 再入决策日，所以「1 月玩完那一刻」的游戏时间是 2 月，日历窗口写 from Y1M2；
        // to 放宽一格到 Y1M3＝纯防御（正常出口必在 month=2 的首次决策日入场命中，读档回 Y1M2 也能补演）。
        // rankBelow 3 保证「开局咖位已达标」（成就商店开局最多买到 Lv.5）的玩家整条不演（2026-10-21 茄子定 §5.1）。
        // （2026-10-25 曾在开头加过一句漂泊锚定句，同日茄子令撤，正文保持原样。）
        // 宿主＝阶段演出（`playOn: 'stageStory'`，全屏场景＋VN；底图＝夜间卧室（`home-night` 键，
        // `bg-home-night.webp`，2026-10-26 茄子供图换入——「晚上九点」的正文配夜景）。
        id: 'FIXED_REVIEW_1_VOW',
        medium: 'encounter',
        playOn: 'stageStory',
        scene: 'home-night',
        trigger: {
            calendar: { from: { year: 1, month: 2 }, to: { year: 1, month: 3 } },
            require: { rankMax: 2 },
        },
        description: '晚上九点，出租屋。手机在泡面碗旁边震起来，备注就一个字：妈。\n妈：{player}，吃饭没？……跟你说个事，你舅妈她们单位政务大厅招人，窗口文员，一个月四千五，五险一金，骑车十分钟到家。舅妈已经把简历递上去了，主任说见过你照片，印象不错。\n你捏着筷子，没接话。泡面在碗里慢慢涨开。\n妈：挂之前你舅妈还问我，孩子在外头到底做什么。我说，拍戏。她"哦"了一声。{player}，那个"哦"，妈夜里想了半天。\n你：（放下筷子）妈，我不想回去。\n妈：四千五，五险一金，干到退休都有。你在外头，这部戏演完了，下部戏在哪儿？\n你：那再找呗。\n妈：再找，再等，再一年。{player}，这行吃的是青春饭，还能等几年？\n你：可是我想演戏。\n妈：妈不是非要你回来……（顿了一下）妈是怕你在这行耗到三十岁，回头想找份安稳工作，人家一问年龄都不要你了。\n你没马上答话。碗里的泡面泡成了一坨，早没了热气。\n你：（妈说得难听，可每一句都是我夜里自己想过的事。）\n妈：这样。妈给你三年。这三年里妈不打电话催你，那个岗的事，妈也不提了。\n你：那好，三年后我 25 岁。到那天，我的咖位会达到[[圈内眼熟]]。要是做不到，不用你催，我自己买票回家。上班、相亲，你安排什么我都接着。\n电话那头静了几秒，久到你以为信号断了。\n妈：……行。妈把日子记本子上。\n妈：就一条——别糊弄自己，做不到就回来。还有，别为了减肥不吃饭。\n你挂了电话，在手机里建了一条提醒，日期设在三年后的今天。\n你：（三年后，[[圈内眼熟]]——让观众有人能记住我的名字，做不到就回老家。）',
        setFlags: ['FLAG_MOTHER_VOW'],
    },
    {
        // 【正式文案 2026-10-21 茄子手写】审查一·25 岁通过回响《那条提醒》（源文档 §1.2）
        // 触发口＝`stage: 2`：阶段一在 25 岁判定通过后 `currentStage` 才变 2，所以它同时表示「审查已通过」。
        // 前置 FLAG_MOTHER_VOW 保证「开局已到 Lv.3」而跳过 1.1 的玩家，不会收到一通没立过约的电话。
        // 重复扫描无害：fire 对「已交付/已在队」幂等（月末 checkPending 也会扫到它，是给漏演的兜底）。
        // 底图＝夜间卧室 `home-night`（2026-10-26 茄子令：审查一三段全换，正文都是「晚上九点」的电话）。
        id: 'FIXED_REVIEW_1_PASS',
        medium: 'encounter',
        playOn: 'stageStory',
        scene: 'home-night',
        trigger: {
            require: { stage: 2 },
            requireFlags: ['FLAG_MOTHER_VOW'],
        },
        description: '三年到了。你差点忘了那个日子，但妈妈没有。晚上九点，手机震起来，还是那个字：妈。\n妈：前天你舅妈在手机上刷到你，她指着问这是不是{player}，我说是。她放大看了半天说，哎哟，还真演上了。\n妈：{player}，妈不懂你们这行，可妈看得出来，你在进步。以前妈追着问你演了什么，你也说不清；现在一部接一部，能站住了。\n妈：妈嘴笨，说不出什么好听的。反正妈心里有数——我闺女，干得不赖。\n那头传来电视的声音，很响，是广告。\n妈：{player}，三年了，妈一天一天数着。今天该翻篇儿了。\n你：妈，等这部戏播了，你来看首映吧。\n妈：（笑了一声）好啊。那妈可得做个头发，不给你丢脸。\n妈：不早了，睡吧。别熬夜，钱不够吱声。\n你挂了电话，翻出手机，把那条设了三年的提醒删了。\n你：（约定完成。从今往后，再也没有人会劝你放弃演艺事业。）',
    },
    {
        // 【正式文案 2026-10-21 茄子手写】审查一·25 岁失败演出《回家的票》（源文档 §1.3）
        // 无 trigger：由阶段表的 `failEvent` 显式派发（`GameLoop.checkStagePass` 判定失败那一刻）。
        // 派发时落「待演」标记，终局等本场演完（`GameLoop.resolvePendingStageFail`），不许先切结算页。
        // 底图＝夜间卧室 `home-night`（2026-10-26 茄子令：审查一三段全换，正文都是「晚上九点」的电话）。
        id: 'FIXED_REVIEW_1_FAIL',
        medium: 'encounter',
        playOn: 'stageStory',
        scene: 'home-night',
        description: '三年到了。这一次，电话是你先打的。\n晚上九点，出租屋。行李箱摊在地上，衣服叠了一半。\n你坐在床沿，手机握在手里，屏幕亮了又暗。那个号码，你按了三遍，又删了三遍，第四遍才拨出去。\n只响了一声，就通了。\n妈：{player}？\n你：妈。\n你在心里排练了一晚上的话，到了嘴边只剩下一句。\n你：我买票回家。\n那头静了几秒。你听见电视被关掉的声音。\n你：妈，你说点什么吧。骂我也行。\n妈：骂你干什么。路是你自己挑的，票也是你自己买的，妈还有什么好说的。几点的票？\n你：还没买呢。\n妈：明天再买，天黑，别赶夜路。房间妈已经收拾出来了，被子昨天刚晒过。\n你：妈，对不起。\n妈：（停了一下）跟妈说这个干什么？回来就好。\n妈：想吃什么？妈明天去买。\n你：……都行。\n妈：那就包饺子。\n你挂了电话，在购票软件里买下明天最早的一班。\n你：（三年，我的演员生涯就到此为止了。）',
    },
    {
        // 【首条通告钩子事件 2026-10-24】后援会·杀青应援微博：我的戏份拍完那一周（NOTICE_PLOT_HOOKS
        // 的 drama+done 命中，**表行不写 chance＝必发**（概率版 0.6 同日已撤）——登记在 NoticePlotHooks.ts，本条 trigger 留空）。
        // 通用事件纪律（见 NoticePlotHooks.ts 表头）：repeatable 必写（一个 id 服务所有剧，一次性灯会吞掉
        // 第二部剧的微博）；文案不写死剧名角色名——{script}/{role} 钩子带入、%玩家名% 玩家本名（后援会
        // 官方号写法同数值事件后援会三条：authorAvatar:'player' 挂玩家捏脸）；评论区只有粉丝一份，不分口碑档。
        // buzz 不填：走挂剧回退（workHeatOf 活尺）——杀青时未开播＝剧本底数档，话题度量级跟剧走。
        // 数值（可调）：小甜头 fame+6，弹卡打开时随白底气泡飘出（口径同数值事件；文案 2026-11-03 茄子改定，撤口碑）。
        // 宿主缺省＝执行档：杀青信号在 runWeek 内发射，月度执行页当周弹出。
        id: 'NOTICE_DRAMA_WRAP',
        medium: 'media',
        repeatable: true,
        steps: [{
            medium: 'media',
            media: {
                kind: 'weibo',
                author: '%玩家名%后援会',
                authorAvatar: 'player',
                body: '#%玩家名%# #%玩家名% {role}# 今日官宣杀青！{role}要和大家暂时说再见了。每一场戏都有始有终，我们会一直等，等播出那天再把她的名字喊一遍。杀青大吉，我们播出见。',
                comments: [
                    '杀青大吉！！坐等播出',
                    '{role}再见，播出见，姐姐下部戏见',
                    '杀青快乐，姐姐好好休息',
                ],
            },
            effects: { fame: 6 },
        }],
    },
    {
        // 【通告钩子事件 2026-11-03】站子拍摄周返图：拍摄周行程执行完那一周，drama+shootWeek 命中、
        // chance 0.25（登记在 NoticePlotHooks.ts）。文案源＝docs/特殊剧情/通告通用事件.md 事件 2。
        // 号主＝前线站子号（不是本会号，但头像同口径挂玩家捏脸：2026-11-03 茄子定，站子号也用人脸图）；
        // 正文不写剧名（下班图本来就拍在剧组门口，报的是人不是戏），repeatable 必写——一个 id 服务所有剧。
        // 宿主缺省＝执行档：信号在 runWeek 内发射，月度执行页当周弹出。数值 fame+5（按周高频，单发低于杀青那条）。
        id: 'NOTICE_DRAMA_FANSITE',
        medium: 'media',
        repeatable: true,
        steps: [{
            medium: 'media',
            media: {
                kind: 'weibo',
                author: '%玩家名%前线Studio',
                authorAvatar: 'player',
                body: '#%玩家名%# 剧组下班返图九张，今天收工晚，路灯下补的两张没修。生图很扛，原图不发，已加水印。',
                comments: [
                    '美我一大跳',
                    '水印一如既往地防代拍',
                    '辛苦啦，早点回去休息',
                ],
            },
            effects: { fame: 5 },
        }],
    },
    {
        // 【通告钩子事件 2026-11-03】播出期轮博护航：开播当月的**月初**发（ScriptSystem.notifyPremiereMonth），
        // drama+premiere 命中、必发、per 'month'、cond {rank:5, billingMax:1, genres:['tv','web']}。
        // 宿主 `decision`＝点「确认行程」之后、行程执行之前的决策页档（与播出侧特殊剧情同一档，发射顺序保证特殊剧情在前）。
        // 数值＝**该剧热度 +10**（2026-11-03 茄子定，走 scriptHeat 的 plotHeatBonus 账、无封顶）：
        // `scriptId` 缺省＝本次派发带入的那部剧（钩子 payload，与 {script} 插值同一个数据源）。
        // buzz 不填＝挂剧回退＝该剧当前热度（轮博把自家戏的热度往上推，读数与账本同源）。
        id: 'NOTICE_DRAMA_PATROL',
        medium: 'media',
        playOn: 'decision',
        repeatable: true,
        steps: [{
            medium: 'media',
            media: {
                kind: 'weibo',
                author: '%玩家名%后援会',
                authorAvatar: 'player',
                body: '#%玩家名%# #%玩家名% {script}# 今晚 20:00，轮博组已排班。带话题发原创，十五字以上，@一次，别刷重复句。广场见。',
                comments: [
                    '已跟完一轮，明晚继续',
                    '原创写好存草稿了',
                    '重复句会被夹 大家注意',
                ],
            },
            scriptHeat: [{ delta: 10 }],
        }],
    },
    {
        // 【正式文案 2026-11-01】后援会线·散粉第一次凑钱应援（源文档 docs/特殊剧情/后援会线.md 事件 1）
        // 一次性（不写 repeatable）：Lv.3 起门槛满足即必发，演完 deliver 自动置灯，不占数值事件总闸与每月一条名额。
        // 散粉自建站账号（头像＝玩家捏脸），评论区只有散粉一份、不分口碑档。
        id: 'FIXED_FANCLUB_1_RAISE',
        medium: 'media',
        playOn: 'decisionEntry',
        trigger: { require: { rank: 3 } },
        steps: [{
            medium: 'media',
            media: {
                kind: 'weibo',
                author: '%玩家名%_Love',
                authorAvatar: 'player',
                body: '#%玩家名%# 我们凑了二十杯咖啡送到剧组，场务说人不在，让放门口了。第一次做应援，有点手忙脚乱，下次会更好。',
                comments: [
                    '辛苦了大家',
                    '这规模也太寒酸了',
                    '慢慢会好起来的',
                ],
            },
            effects: { fame: 6 },
        }],
    },
    {
        // 【正式文案 2026-11-01】后援会线·散粉第一次接机（源文档 docs/特殊剧情/后援会线.md 事件 2）
        // 触发用名气 200（Lv.3 门槛 80 与 Lv.4 门槛 300 的中点）：落在 Lv.3 之后、Lv.4 之前。
        // 一次性；setFlags 供事件 3 的 requireFlags 接灯，保证"先接机、后包场"。
        id: 'FIXED_FANCLUB_2_PICKUP',
        medium: 'media',
        playOn: 'decisionEntry',
        trigger: { require: { fame: 200 } },
        setFlags: ['FLAG_FANCLUB_PICKUP'],
        steps: [{
            medium: 'media',
            media: {
                kind: 'weibo',
                author: '%玩家名%_Love',
                authorAvatar: 'player',
                body: '#%玩家名%# 今天去接机了，来了十几个人，灯牌是昨晚赶出来的。姐姐出来的时候愣了一下，然后朝我们这边疯狂挥手~真人真的超级美比剧里好看太多了呜呜呜呜呜',
                comments: [
                    '和大家面基好开心',
                    '下次我请假也去',
                    '灯牌举反了两个 笑死',
                ],
            },
            effects: { fame: 6 },
        }],
    },
    {
        // 【正式文案 2026-11-01】后援会线·后援会包场看剧（源文档 docs/特殊剧情/后援会线.md 事件 3）
        // 一次性；Lv.4 起可播，且需事件 2 的 FLAG_FANCLUB_PICKUP（先接机、后包场）。
        // 后援会官方号（头像＝玩家捏脸），评论区只有粉丝一份、不分口碑档。
        id: 'FIXED_FANCLUB_3_SCREENING',
        medium: 'media',
        playOn: 'decisionEntry',
        trigger: { require: { rank: 4 }, requireFlags: ['FLAG_FANCLUB_PICKUP'] },
        steps: [{
            medium: 'media',
            media: {
                kind: 'weibo',
                author: '%玩家名%后援会',
                authorAvatar: 'player',
                body: '#%玩家名%# 【包场应援】本次包场两场已全部结束，两场均满座。感谢每一位到场的你，票根已由管理组统一整理留存。',
                comments: [
                    '第一场散场大家都在拍票根',
                    '下次提前通知 我请假',
                    '辛苦管理组',
                ],
            },
            effects: { fame: 6 },
        }],
    },
    {
        // 【正式文案 2026-11-01】后援会线·混剪视频被本尊转发（源文档 docs/特殊剧情/后援会线.md 事件 4）
        // 一次性（不写 repeatable）：2026-11-03 由数值事件 NE_FANMIX 迁入本线，只改通道不动门槛——
        // Lv.4 起必发、名气 +8 不变；与事件 3 同档，数组顺序保证包场先演、混剪后演（同月可连播）。
        // 散粉自建站账号（头像＝玩家捏脸），评论区只有粉丝一份、不分口碑档。
        id: 'FIXED_FANCLUB_4_FANMIX',
        medium: 'media',
        playOn: 'decisionEntry',
        trigger: { require: { rank: 4 } },
        steps: [{
            medium: 'media',
            media: {
                kind: 'weibo',
                author: '%玩家名%_Love',
                authorAvatar: 'player',
                body: '#%玩家名%# 花了一个月剪的视频，竟然被姐姐本人转发了！好感动QAQ谢谢每一个看完的人~',
                comments: [
                    '剪得真的很好 三刷了',
                    '她居然看到了',
                    '能不能出个续集',
                ],
            },
            effects: { fame: 8 },
        }],
    },
];
